import React, { useState } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { Text, toast } from "@jsq/ds/core";
import { ProfileNameFields } from "~/pages/Compliance/utils/types";
import { PaymentBeneficiaryStatus } from "../cellStatus/PaymentBeneficiaryStatus";
import { PositionData } from "../types";
import PaymentBeneficiaryModal from "./PaymentBeneficiaryModal";
import { cellText } from "./styles_vanilla.css";
import { transformToCompliancePaymentAccount } from "./utils";

interface PaymentBeneficiaryCellRendererProps extends ICellRendererParams {
  data: PositionData;
}

const PaymentBeneficiaryCellRenderer: React.FC<PaymentBeneficiaryCellRendererProps> = ({
  data,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  if (!data || !data.caseId) {
    return <Text className={cellText} data-testid="empty-cell"></Text>;
  }

  const compliancePaymentAccountData = transformToCompliancePaymentAccount(data);

  const isGroupRow = data.successorCases && data.successorCases.length > 0;
  const isChildRow = data.parentId !== undefined;
  const isNotAttached =
    !data.linkedOnboardingCase ||
    ((!data.linkedOnboardingCase.profiles || data.linkedOnboardingCase.profiles.length === 0) &&
      !data.linkedOnboardingCase.investingProfile);

  if (isGroupRow) {
    return <Text className={cellText}></Text>;
  }

  if (isNotAttached || isChildRow) {
    return <PaymentBeneficiaryStatus status="not-applicable" />;
  }

  if (data.readonly) {
    if (data.linkedProfile) {
      return (
        <PaymentBeneficiaryStatus
          status="manual-match-hide-view"
          content={
            data.linkedProfile.legalName ??
            data.linkedProfile.firstName + " " + data.linkedProfile.lastName
          }
          onEditClick={() => {
            toast.error("Assign case to yourself to edit payment beneficiary.");
          }}
        />
      );
    }
    return <PaymentBeneficiaryStatus status="add-payment-disabled" />;
  }

  if (data.linkedProfile === null) {
    return (
      <>
        <PaymentBeneficiaryStatus
          status="add-payment-default"
          onAddClick={() => setShowAddModal(true)}
        />
        <PaymentBeneficiaryModal
          title="Add"
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          mode="create"
          profiles={data.linkedOnboardingCase?.profiles}
          investingProfile={
            data.linkedOnboardingCase?.investingProfile as ProfileNameFields | null
          }
          programId={data.linkedOnboardingCase?.program?.id}
          compliancePaymentAccount={compliancePaymentAccountData}
          caseId={data.caseId}
          positionId={data.positionId ?? ""}
          createdProfile={data.createdProfile}
        />
      </>
    );
  }

  return (
    <>
      <PaymentBeneficiaryStatus
        status="manual-match"
        content={
          data.linkedProfile
            ? (data.linkedProfile.legalName ??
              data.linkedProfile.firstName + " " + data.linkedProfile.lastName)
            : ""
        }
        onEditClick={() => setShowEditModal(true)}
      />
      <PaymentBeneficiaryModal
        title="Edit"
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        mode="edit"
        profiles={data.linkedOnboardingCase?.profiles}
        investingProfile={
          data.linkedOnboardingCase?.investingProfile as ProfileNameFields | null
        }
        programId={data.linkedOnboardingCase?.program?.id}
        compliancePaymentAccount={compliancePaymentAccountData}
        caseId={data.caseId}
        positionId={data.positionId ?? ""}
        createdProfile={data.createdProfile}
      />
    </>
  );
};

export default PaymentBeneficiaryCellRenderer;
